import React, { useEffect, useRef, useState } from 'react';

// Utils
import PropTypes from 'prop-types';
import cn from 'classnames';

// Components
import * as S from './Styled';
import Icon from '../Icon';

// Utils
import _find from 'lodash/find';

const propTypes = {
  items: PropTypes.array.isRequired,
  caption: PropTypes.string,
  withIcon: PropTypes.bool,
  highlighting: PropTypes.bool,
};

const Selecter = ({
  items,
  withIcon,
  caption,
  highlighting,
  valColor,
  onSelect,
  fontSize,
  activeItem,
  extra,
  suffix,
  withSearch = false,
  searchPlaceholder = 'Пошук...',
}) => {
  const [open, setOpen] = useState(false);
  const [item, setItem] = useState(activeItem ?? items[0]);
  const [searchQuery, setSearchQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  const filteredItems =
    withSearch && searchQuery
      ? items.filter(i =>
          i.title.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : items;

  useEffect(() => {
    if (activeItem !== undefined) {
      setItem(activeItem);
    } else {
      setItem(items[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  useEffect(() => {
    if (withSearch) setCursor(0);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || !listRef.current) return;
    const els = listRef.current.querySelectorAll('[data-selecter-item]');
    els[cursor]?.scrollIntoView({ block: 'nearest' });
  }, [cursor, open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      const idx = items.findIndex(i => i.id === item?.id);
      setCursor(idx >= 0 ? idx : 0);
      if (withSearch) setTimeout(() => searchRef.current?.focus(), 0);
    }
    if (!next) setSearchQuery('');
  };

  const close = () => {
    setOpen(false);
    setSearchQuery('');
  };

  const selectByIndex = index => {
    const selected = filteredItems[index];
    if (!selected || selected.disabled) return;
    setItem(selected);
    onSelect(selected.id);
    close();
  };

  const handleSelect = (event, id) => {
    event.preventDefault();
    const found = _find(filteredItems, item => item.id === id);
    if (!found || found.disabled) return;
    setItem(found);
    onSelect(found.id);
    close();
  };

  const handleKeyDown = e => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggle();
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setCursor(prev => Math.min(prev + 1, filteredItems.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setCursor(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        selectByIndex(cursor);
        break;
      case 'Escape':
      case 'Tab':
        close();
        break;
      default:
        break;
    }
  };

  return (
    <S.Selecter
      className={cn('selecter', { 'selecter-open': open })}
      onKeyDown={handleKeyDown}
    >
      {typeof caption !== 'undefined' && <S.Caption>{caption}</S.Caption>}
      <S.Select>
        <S.Value
          fontSize={fontSize}
          onClick={toggle}
          className={cn({ 'selecter-selected': item })}
          valColor={valColor}
        >
          {extra ? (
            extra
          ) : (
            <>
              {withIcon ? (
                <S.IconHolder>
                  {item.iconName != null ? (
                    <Icon name={item.iconName} size="middle" />
                  ) : (
                    <Icon name="up" size="middle" />
                  )}
                </S.IconHolder>
              ) : null}
              <S.TextInner>{item.title}{suffix}</S.TextInner>
            </>
          )}
          <S.ArrowBold name="up_bold" />
        </S.Value>
        <S.List className={cn('list', { highlighting })} ref={listRef}>
          {withSearch && (
            <S.SearchWrapper>
              <S.SearchInput
                ref={searchRef}
                type="text"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onClick={e => e.stopPropagation()}
              />
            </S.SearchWrapper>
          )}
          {withSearch && filteredItems.length === 0 ? (
            <S.NoResults>Нічого не знайдено</S.NoResults>
          ) : (
            filteredItems.map(({ id, title, disabled, iconName }, index) => (
              <S.Item
                key={id}
                data-selecter-item
                className={cn({
                  active: item.id === id,
                  'is-highlighted': index === cursor,
                })}
                disabled={disabled}
                onClick={event => handleSelect(event, id)}
                fontSize={fontSize}
              >
                {withIcon && iconName ? (
                  <Icon name={iconName} medium />
                ) : null}
                <S.Text>{title}{suffix}</S.Text>
                <S.ItemIcon name="check" tiny color="#253858" />
              </S.Item>
            ))
          )}
        </S.List>
      </S.Select>
      <S.Overlay onClick={close} />
    </S.Selecter>
  );
};

Selecter.propTypes = propTypes;

export default Selecter;
